import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { trackEvent } from "@/lib/analytics";
import { renderSummary } from "@/lib/renderSummary";

const H2 = ({ children }) => <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-5 mt-12" style={{ fontFamily: "Outfit" }}>{children}</h2>;

// "de Le Cid" -> "du Cid", "de Les Misérables" -> "des Misérables".
export const deTitle = (title) => title.startsWith("Le ") ? `du ${title.slice(3)}` : title.startsWith("Les ") ? `des ${title.slice(4)}` : `de ${title}`;

const Cta = ({ slug }) => (
  <div className="my-12 border-2 border-[#002FA7] p-6 md:p-8 bg-[#002FA7]/[0.04]" data-testid="oeuvre-cta">
    <p className="font-black text-xl md:text-2xl tracking-tight mb-2" style={{ fontFamily: "Outfit" }}>Votre cours sur ce livre est long ?</p>
    <p className="text-muted-foreground mb-5">Collez-le ou prenez-le en photo : SUX1 en fait une fiche de révision, une version DYS ou un quiz en 5 secondes.</p>
    <div className="flex flex-wrap gap-3">
      <Link to="/" onClick={() => trackEvent("oeuvre_cta_click", { slug })} data-testid="oeuvre-cta-try"
        className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-3 font-bold hover:bg-[#00237d] transition-colors">
        Résumer mon cours <ArrowRight className="w-4 h-4" />
      </Link>
      <Link to="/?format=revision_dys" data-testid="oeuvre-cta-dys" className="px-5 py-3 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors">Version DYS</Link>
    </div>
    <p className="mt-3 text-xs text-muted-foreground">Exemples gratuits, puis dès 1 € les 10 résumés (pack découverte).</p>
  </div>
);

export default function Oeuvre() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setW(null); setMissing(false);
    fetch(`/oeuvres/${slug}.json`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setW).catch(() => setMissing(true));
  }, [slug]);
  useSeo(w && `Résumé ${deTitle(w.title)} (${w.author}) : personnages, thèmes, citations · SUX1`, w?.meta_description);

  if (missing) return <div className="p-10" data-testid="oeuvre-missing">Œuvre introuvable. <Link to="/oeuvres" className="underline text-[#002FA7]">Voir toutes les œuvres</Link></div>;
  if (!w) return <div className="p-10 text-muted-foreground">Chargement…</div>;

  return (
    <article className="min-h-screen bg-card" data-testid="oeuvre-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 lg:py-16">
        <Link to="/oeuvres" className="font-mono uppercase tracking-[0.2em] text-[11px] flex items-center gap-2 hover:text-[#002FA7] mb-8" data-testid="oeuvre-back"><ArrowLeft className="w-3 h-3" /> Toutes les œuvres</Link>
        <div className="sux-marker mb-3">{w.author} · {w.year} · {w.genre} · {w.level}</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-5" style={{ fontFamily: "Outfit" }} data-testid="oeuvre-title">Résumé {deTitle(w.title)}</h1>
        <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{w.intro}</p>

        <H2>Le résumé en bref</H2>
        <p className="leading-relaxed" data-testid="oeuvre-short">{w.short}</p>

        <H2>Résumé détaillé</H2>
        <div className="space-y-6" data-testid="oeuvre-parts">
          {w.parts.map((p) => <section key={p.title}><h3 className="font-bold text-lg mb-1">{p.title}</h3><p className="leading-relaxed text-[15px]">{p.text}</p></section>)}
        </div>

        <Cta slug={w.slug} />

        {w.dys && (
          <section className="mt-12 border-2 border-[#002FA7]" data-testid="oeuvre-dys">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-border bg-[#002FA7] text-white">
              <span className="font-black tracking-tight text-lg" style={{ fontFamily: "Outfit" }}>Version DYS, prête à lire</span>
              <span className="font-mono uppercase tracking-[0.14em] text-[10px] opacity-90">Phrases courtes · mots expliqués · je me teste</span>
            </div>
            <div className="p-5 md:p-6 summary-content notranslate text-[16px] leading-[1.8]" data-testid="oeuvre-dys-content"
              dangerouslySetInnerHTML={{ __html: renderSummary(w.dys) }} />
          </section>
        )}

        <H2>Les personnages</H2>
        <ul className="space-y-2" data-testid="oeuvre-characters">{w.characters.map((c) => <li key={c.name}><b>{c.name}</b> : {c.desc}</li>)}</ul>

        <H2>Les thèmes</H2>
        <ul className="space-y-3" data-testid="oeuvre-themes">{w.themes.map((t) => <li key={t.name}><b>{t.name}</b> : {t.desc}</li>)}</ul>

        <H2>Citations à connaître</H2>
        <div className="space-y-4" data-testid="oeuvre-quotes">
          {w.quotes.map((q) => (
            <blockquote key={q.text} className="border-l-4 border-[#FF2A00] pl-4">
              <p className="italic flex gap-2"><Quote className="w-4 h-4 shrink-0 text-[#FF2A00] mt-1" />« {q.text} »</p>
              <p className="text-sm text-muted-foreground mt-1">{q.context}</p>
            </blockquote>
          ))}
        </div>

        <H2>À retenir pour le brevet ou le bac</H2>
        <ul className="space-y-2 list-disc pl-5" data-testid="oeuvre-key-points">{w.key_points.map((k) => <li key={k}>{k}</li>)}</ul>

        <Cta slug={w.slug} />
        <p className="text-xs text-muted-foreground">Fiche rédigée avec l'aide de l'IA de SUX1. Vérifiez toujours avec votre édition du texte et votre cours.</p>
      </div>
    </article>
  );
}
